package com.castly.admin.ui.preview

import androidx.compose.runtime.*
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * PreviewUserSwitcher — displays the current preview user ID (truncated UUID),
 * with a tap-to-edit input allowing manual UUID entry and a "Generate New" button.
 *
 * @param currentUserId The current preview user UUID
 * @param onChangeUserId Called when user manually enters or generates a new UUID
 */
@Composable
fun PreviewUserSwitcher(
    currentUserId: String,
    onChangeUserId: (String) -> Unit
) {
    var isEditing by remember { mutableStateOf(false) }
    var editValue by remember(currentUserId) { mutableStateOf(currentUserId) }

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(Spacing.xs)
        }
    }) {
        if (isEditing) {
            // Edit mode: input field + save/cancel
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(Spacing.xs)
                    position(Position.Absolute)
                    top(100.percent)
                    right(0.px)
                    property("z-index", "100")
                    backgroundColor(Color("#1f2937"))
                    borderRadius(8.px)
                    padding(Spacing.md)
                    property("box-shadow", "0 4px 12px rgba(0,0,0,0.3)")
                    width(260.px)
                }
            }) {
                Span({
                    style {
                        fontSize(Typography.xs)
                        color(Color("#9ca3af"))
                        marginBottom(Spacing.xs)
                    }
                }) { Text("Preview User ID") }

                TextInput(editValue) {
                    placeholder("Enter UUID...")
                    onInput { editValue = it.value }
                    style {
                        width(100.percent)
                        padding(Spacing.sm)
                        fontSize(Typography.xs)
                        fontFamily("monospace")
                        backgroundColor(Color("#111827"))
                        color(Color("#ffffff"))
                        border(1.px, LineStyle.Solid, Color("#374151"))
                        borderRadius(4.px)
                        property("box-sizing", "border-box")
                        property("outline", "none")
                    }
                }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(Spacing.xs)
                        marginTop(Spacing.xs)
                    }
                }) {
                    // Apply button
                    Button({
                        onClick {
                            if (editValue.isNotBlank()) {
                                onChangeUserId(editValue.trim())
                                isEditing = false
                            }
                        }
                        style {
                            flex(1)
                            padding(Spacing.xs, Spacing.sm)
                            backgroundColor(Color("#7c3aed"))
                            color(Color("#ffffff"))
                            border(0.px)
                            borderRadius(4.px)
                            cursor("pointer")
                            fontSize(Typography.xs)
                            fontWeight("600")
                        }
                    }) { Text("Apply") }

                    // Generate New button
                    Button({
                        onClick {
                            val newId = js("crypto.randomUUID()").unsafeCast<String>()
                            editValue = newId
                            onChangeUserId(newId)
                            isEditing = false
                        }
                        style {
                            flex(1)
                            padding(Spacing.xs, Spacing.sm)
                            backgroundColor(Color("#059669"))
                            color(Color("#ffffff"))
                            border(0.px)
                            borderRadius(4.px)
                            cursor("pointer")
                            fontSize(Typography.xs)
                            fontWeight("600")
                        }
                    }) { Text("New") }

                    // Cancel button
                    Button({
                        onClick {
                            editValue = currentUserId
                            isEditing = false
                        }
                        style {
                            padding(Spacing.xs, Spacing.sm)
                            backgroundColor(Color.transparent)
                            color(Color("#9ca3af"))
                            border(1.px, LineStyle.Solid, Color("#374151"))
                            borderRadius(4.px)
                            cursor("pointer")
                            fontSize(Typography.xs)
                        }
                    }) { Text("X") }
                }
            }
        }

        // User icon + truncated ID button (always visible)
        Button({
            onClick { isEditing = !isEditing }
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(Spacing.xs)
                backgroundColor(Color("rgba(255,255,255,0.15)"))
                color(Color("#ffffff"))
                border(0.px)
                borderRadius(4.px)
                cursor("pointer")
                fontSize(Typography.xs)
                fontFamily("monospace")
                padding(Spacing.xs, Spacing.sm)
            }
        }) {
            Span({ style { fontSize(12.px) } }) { Text("\uD83D\uDC64") } // 👤
            Text(currentUserId.take(8))
        }
    }
}
